import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useContactsMergeFields } from 'hooks/useMergeFieldsBase';
import PerformActionService from 'services/PerformActionService';
import LibraryService, { templateForApi } from 'services/LibraryService';
import { getPayloadFieldValue } from 'services/FieldService';
import { FIELD_TYPES } from 'utility/constants';
import {
  isContactTagsField,
  isContactTitlesField,
  isLastNameField,
} from 'checks/fields';
import { isTimeZoneField, isEmailStatusField } from 'checks/fields';
import { validate as isEmail } from 'components/Inputs/TextInput/presets/EmailAddress';
import { toastVariant, useToast, toastDelay } from 'components/ToastProvider';
import BasicModalWithConfirmation from 'components/Modals/presets/BasicModalWithConfirmation';
import Button from 'components/Button';
import { isClientObject } from 'components/Modals/utilities';
import {
  useSendEmailStep,
  sendEmailStepIsValid,
} from './StepsContent/SendEmail';
import { GroupedActionButtons } from './GroupedActionButtons';
import {
  useGetSummary,
  SEND_TEXT_IDENTIFIER,
  CHANGE_TAGS_IDENTIFIER,
  MANAGE_TEAM_IDENTIFIER,
  MANAGE_SUBSCRIPTIONS,
  EXPORT_IDENTIFIER,
  ARCHIVE_IDENTIFIER,
  CHANGE_FIELD_IDENTIFIER,
  SEND_EMAIL_IDENTIFIER,
  SEND_SURVEY_IDENTIFIER,
  MODIFY_AUTOMATION_IDENTIFIER,
  START_AGENTIC_WORKFLOW_IDENTIFIER,
  CUSTOM_ACTION_IDENTIFIER,
  SECONDARY_ACTIONS,
  SMART_CONNECTOR_IDENTIFIER,
} from './model';
import SelectedActionStep from './SelectedActionStep';
import SummaryStep from './SummaryStep';
import { PerformActionStepper } from './PerformActionStepper';
import { getDefaultColumnSettings } from 'components/Wizards/CustomObject/steps/CustomLayout/subsections/DefaultColumns/helpers';
import { mergeColumnSettings } from 'pages/ColumnsPage/helpers';
import { ConfrimBulkActionModal } from './ConfirmBulkActionModal';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { StyledModalBody, StyledModalFooter } from './styles';
import SmartConnectorService from 'services/SmartConnectorService';

const CONFIRM_BULK_ACTION_THRESHOLD = 100000;

const getOptionValue = (opt) => opt && opt.value;

const getValuesArray = (arr) => arr && arr.map((x) => getOptionValue(x));

const getArrayOfIds = (arr) => arr.map((e) => e.id);

const createPayload = ({
  key,
  isContacts,
  objectId,
  action,
  data = {},
  resumePausedAutomations,
  sendEmailNotification,
}) => ({
  ...data,
  [isContacts ? 'clientKey' : 'entityRecordsSetKey']: key,
  ...(!isContacts ? { objectId } : {}),
  action,
  resumePausedAutomations,
  sendEmailNotification,
});

export default function PerformActionModal({
  filters,
  search = '',
  selection,
  model,
  groupId = null,
  groupOptions,
  allCount,
  records,
  recordsCount,
  selectedAction = null,
  performActionOptions,
  groupDefs,
  onHide,
  onSelect,
  onResetSelection,
  savedColumnSettings,
  fields,
  ...props
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [dirty, setDirty] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    if (selectedAction === null) {
      return 1;
    }
    return 2;
  });
  const [steps, setSteps] = useState(() => {
    if (!selectedAction) {
      return [];
    }
    const step = performActionOptions.find(
      (el) =>
        selectedAction.value === el.value &&
        selectedAction.customAction?.id === el.customAction?.id
    );
    return step.steps;
  });
  const [performingAction, setPerformingAction] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showToast] = useToast();
  const confirmClickedRef = useRef(false); // flag to stop getSummaryFn being called after confirm has been clicked
  // start automation
  const [automation, setAutomation] = useState('');
  const [secondaryAction, setSecondaryAction] = useState(
    SECONDARY_ACTIONS.START
  );
  const [resumePausedAutomations, setResumePausedAutomations] = useState(false);

  const clientObject = useSelector(getBusinessClientObject);
  const clientObjectId = clientObject.id;

  // send surveys
  const [surveyValue, setSurveyValue] = useState('');

  // Send Text Action
  const [sendTextValue, setSendTextValue] = useState({
    content: '',
    htmlContent: '',
  });
  const [sendTextName, setSendTextName] = useState('');

  // Change Tags Action
  const [tagsToAdd, setTagsToAdd] = useState([]);
  const [tagsToRemove, setTagsToRemove] = useState([]);
  const tagsField = useMemo(() => fields.find(isContactTagsField), [fields]);

  // Manage Team Members Action
  const [teamMembers, setTeamMembers] = useState([]);
  const [grantMyRecordsAccess, setGrantMyRecordsAccess] = useState(true);

  // Manage Subscription Action
  const [subscriptionItem, setSubscriptionItem] = useState(null);
  const [status, setSubscriptionStatus] = useState('');

  // export
  const [selectedOption, setSelectedOption] = useState(null);
  const [includeKizenId, setIncludeKizenId] = useState(true);
  const [summarizeRelationships, setSummarizeRelationships] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);

  // Run smart connector
  const [smartConnector, setSmartConnector] = useState(null);

  const defaultColumnSettings = useMemo(
    () => (model ? getDefaultColumnSettings(model, fields, t) : []),
    [fields, model, t]
  );
  const columnSettings = useMemo(() => {
    return mergeColumnSettings(defaultColumnSettings, savedColumnSettings);
  }, [savedColumnSettings, defaultColumnSettings]);

  // Manage Field Change
  const [field, setField] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [resolution, setResolutionValue] = useState(null);
  const [removeTags, setRemoveTags] = useState([]);
  const sendEmailStep = useSendEmailStep();
  const isContacts = isClientObject(model);

  const objectInfo = useMemo(() => {
    return {
      isContacts: isContacts,
      entityName: model.entityName,
      names: isContacts
        ? {
            singular: t('Contact'),
            plural: t('Contacts'),
          }
        : {
            singular: t('Record'),
            plural: t('Records'),
          },
      defaultTitleData: {
        affix: t('Perform Action on'),
        text: `${t('All')} ${isContacts ? t('Contacts') : t('Records')}`,
      },
    };
  }, [isContacts, model.entityName, t]);

  const getTitleText = useCallback(() => {
    if (selection.checkedCount === allCount) {
      return objectInfo.defaultTitleData.text;
    }

    const recordsStr =
      selection.checkedCount === 1
        ? objectInfo.names.singular
        : objectInfo.names.plural;
    const entityStr = objectInfo.isContacts
      ? ' '
      : ` ${objectInfo.entityName} `;

    if (selection.value === 'group' && groupId) {
      const group = groupOptions.find((el) => el.id === groupId);
      const text = `${selection.checkedCount}${entityStr}${recordsStr} ${t(
        'In Group'
      )} ${group ? `"${group.name}"` : ''}`;
      return text;
    } else if (
      (selection.value === 'search' || selection.value === 'page') &&
      records.length
    ) {
      const text = `${selection.checkedCount} ${t(
        'Selected'
      )}${entityStr}${recordsStr}`;
      return text;
    } else if (
      (selection.value === 'individual' && selection.checkedCount !== 0) ||
      selection.checkedCount !== recordsCount
    ) {
      const text = `${selection.checkedCount} ${t(
        'Selected'
      )}${entityStr}${recordsStr}`;
      return text;
    }
  }, [
    groupId,
    recordsCount,
    groupOptions,
    selection,
    records.length,
    allCount,
    objectInfo,
    t,
  ]);

  const [title, setTitle] = useState(() => {
    const title = objectInfo.defaultTitleData;
    if (!selectedAction) {
      return title;
    }
    const step = performActionOptions.find(
      (el) =>
        selectedAction.value === el.value &&
        selectedAction.customAction?.id === el.customAction?.id
    );
    const text = getTitleText();
    return { text: text || title.text, affix: step.titleForModal };
  });

  const { categorizedFieldOptions: sendTextMergeFields } =
    useContactsMergeFields();

  // There are so many dependencies of summary fetching that we're
  // just going to always update the function, toss it in a ref, and
  // only call it actively when moving to the final step, rather than
  // reactively in an effect.
  const getSummary = useRef();
  const { subscriptionLists, subscriptionListOptions } = sendEmailStep;
  const [{ summary, isFetching, isPolling }, getSummaryFn] = useGetSummary({
    objectId: model.id,
    selection,
    records,
    groupOptions,
    groupId,
    search,
    filters,
    field,
    fieldValue,
    selectedAction,
    performActionOptions,
    subscriptionLists,
    subscriptionListOptions:
      selectedAction?.value === SEND_EMAIL_IDENTIFIER &&
      subscriptionListOptions,
    status,
    subscriptionItem,
    secondaryAction,
    automation,
    customAction: selectedAction?.customAction,
    smartConnector,
  });

  const [sendEmailNotification, setSendEmailNotification] = useState(true);

  getSummary.current = getSummaryFn;

  const goToSelectActionStep = useCallback(() => {
    setCurrentStep(1);
    setDirty(false);
    setPendingAction(null);
    setTitle((ps) => ({ ...ps, affix: t('Perform Action on') }));
    onSelect(null);
  }, [t, onSelect]);

  const goToConfigureActionStep = useCallback(() => {
    setCurrentStep(2);
    setDirty(false);
  }, []);

  const goToSummaryStep = useCallback(() => {
    // block updates after action confirmed

    if (!confirmClickedRef.current) {
      getSummary.current();
    }
    setCurrentStep(3);
    setDirty(true);
  }, []);

  useEffect(() => {
    if (selection.checkedCount === allCount) {
      setTitle(objectInfo.defaultTitleData);
      return;
    }

    const text = getTitleText();

    if (text) {
      setTitle((ps) => ({ ...ps, text }));
    }
  }, [
    getTitleText,
    selection.checkedCount,
    allCount,
    objectInfo.defaultTitleData,
  ]);

  useEffect(() => {
    setFieldValue('');
    if (removeTags.length) {
      setRemoveTags([]);
    }
  }, [field]); // eslint-disable-line react-hooks/exhaustive-deps

  const disableConfigureActionStep =
    (selectedAction && selectedAction.value === ARCHIVE_IDENTIFIER) ||
    selectedAction?.customAction;

  useEffect(() => {
    if (!selectedAction) {
      goToSelectActionStep();
    } else {
      const step = performActionOptions.find(
        (el) =>
          selectedAction.value === el.value &&
          selectedAction.customAction?.id === el.customAction?.id
      );
      setSteps(step.steps);
      setTitle((ps) => ({ ...ps, affix: step.titleForModal }));
      if (disableConfigureActionStep) {
        goToSummaryStep();
      } else {
        goToConfigureActionStep();
      }
    }
  }, [
    selectedAction,
    performActionOptions,
    disableConfigureActionStep,
    goToSelectActionStep,
    goToConfigureActionStep,
    goToSummaryStep,
  ]);

  const entityName = model.entityName;
  const summaryCacheKey = summary?.cache_key;
  const allowedRecordCount = summary?.stats?.valid_count ?? 0;
  const formattedRecordCount = new Intl.NumberFormat(language).format(
    allowedRecordCount
  );

  const handleSendText = async () => {
    try {
      setPerformingAction(true);

      await LibraryService.sendText({
        content: sendTextValue.content,
        name: sendTextName,
        isBulk: true,
        sendTo: summaryCacheKey,
      });
      showToast({
        variant: toastVariant.SUCCESS,
        message: t('Text message sent'),
      });
    } catch (error) {
      showToast({
        variant: toastVariant.FAILURE,
        message: `${t('The text message was not successfully sent.')} ${t(
          'Please try again or contact Kizen support.'
        )}`,
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleSendEmail = async () => {
    try {
      setPerformingAction(true);
      const { email } = sendEmailStep;
      await LibraryService.sendEmail({
        ...templateForApi(email),
        isBulk: true,
        sendTo: summaryCacheKey,
      });
      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'Email queued for {{formattedRecordCount}} {{entityName}} records',
          {
            entityName,
            formattedRecordCount,
          }
        ),
        delay: toastDelay.long,
      });
      onHide();
    } catch {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The email was not successfully queued. Please try again or contact Kizen support.'
        ),
      });
    } finally {
      setPerformingAction(false);
    }
  };

  const handleAutomationAction = async (action = SECONDARY_ACTIONS.START) => {
    try {
      setPerformingAction(true);
      const payload = createPayload({
        data: {
          automationId: automation.value,
        },
        key: summaryCacheKey,
        objectId: objectInfo.isContacts ? clientObjectId : model.id,
        action,
        resumePausedAutomations,
        sendEmailNotification,
      });

      await PerformActionService.modifyAutomation(payload);

      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'Agentic workflow {{noun}} queued for {{formattedRecordCount}} {{entityName}} records',
          {
            entityName,
            formattedRecordCount,
            noun: {
              [SECONDARY_ACTIONS.START]: t('start'),
              [SECONDARY_ACTIONS.PAUSE]: t('pause'),
              [SECONDARY_ACTIONS.CANCEL]: t('cancellation'),
            }[action],
          }
        ),
        delay: toastDelay.long,
      });
    } catch (error) {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The agentic workflows were not successfully {{action}}. Please try again or contact Kizen support.',
          {
            action: {
              [SECONDARY_ACTIONS.START]: t('started'),
              [SECONDARY_ACTIONS.PAUSE]: t('paused'),
              [SECONDARY_ACTIONS.CANCEL]: t('canceled'),
            }[action],
          }
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleCustomAction = async () => {
    const { customAction } = selectedAction;
    try {
      setPerformingAction(true);
      await PerformActionService.performCustomAction({
        entity_records_set_key: summaryCacheKey,
        custom_action_id: customAction.id,
        send_email_notification: sendEmailNotification,
      });

      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          '{{customActionName}} queued for {{formattedRecordCount}} {{entityName}} records',
          {
            customActionName: customAction.name,
            entityName,
            formattedRecordCount,
          }
        ),
        delay: toastDelay.long,
      });
    } catch (error) {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The {{noun}} was not successfully queued. Please try again or contact Kizen support.',
          {
            noun: customAction.name,
          }
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleSendSurvey = async () => {
    try {
      setPerformingAction(true);
      await PerformActionService.saveSendSurvey({
        clientKey: summaryCacheKey,
        id: surveyValue.value,
        sendEmailNotification,
      });
      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'Survey sent to {{formattedRecordCount}} {{entityName}} records',
          {
            entityName,
            formattedRecordCount,
          }
        ),
      });
    } catch (error) {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The surveys were not successfully sent. Please try again or contact Kizen support.'
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleChangeTags = async () => {
    try {
      setPerformingAction(true);
      await PerformActionService.changeTags({
        clientKey: summaryCacheKey,
        tagsToAdd: tagsToAdd.map((tag) => tag.id),
        tagsToRemove: tagsToRemove.map((tag) => tag.id),
        sendEmailNotification,
      });
      const message = t(
        'Tag changes queued for {{formattedRecordCount}} {{entityName}} records',
        { entityName, formattedRecordCount }
      );
      showToast({
        variant: toastVariant.SUCCESS,
        message,
        delay: toastDelay.long,
      });
    } catch {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The tags were not successfully updated. Please try again or contact Kizen support.'
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleManageTeam = async () => {
    try {
      setPerformingAction(true);
      const payload = createPayload({
        data: {
          teamMemberIds: getValuesArray(teamMembers),
          has_record_access: grantMyRecordsAccess,
        },
        key: summaryCacheKey,
        isContacts: objectInfo.isContacts,
        objectId: model.id,
        sendEmailNotification,
      });
      await PerformActionService.manageTeam(payload);
      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'Team members association queued for {{formattedRecordCount}} {{entityName}} records',
          { entityName, formattedRecordCount }
        ),
        delay: toastDelay.long,
      });
    } catch {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The team members were not successfully associated. Please try again or contact Kizen support.'
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleManageSubscriptions = async () => {
    try {
      setPerformingAction(true);
      await PerformActionService.manageSubscriptions({
        clientKey: summaryCacheKey,
        subscriptionListId: subscriptionItem.value,
        status,
        sendEmailNotification,
      });
      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'Subscription list update queued for {{formattedRecordCount}} {{entityName}} records',
          { entityName, formattedRecordCount }
        ),
        delay: toastDelay.long,
      });
    } catch {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The subscription list was not successfully updated. Please try again or contact Kizen support.'
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleExport = async () => {
    try {
      setPerformingAction(true);

      let fieldIds = [];

      if (selectedOption?.value === 'visible') {
        const columnSettingsPayload = [...columnSettings];

        // we want to include both first_name and last_name fields if fullName is visible
        // but ensure ids are not duplicated (like if first_name or last_name is already visible)
        const fullNameIndex = columnSettingsPayload.findIndex(
          (x) => x.id === 'fullName'
        );
        if (fullNameIndex !== -1) {
          const lastNameId = fields.find(isLastNameField)?.id;
          const lastNameIndex = columnSettingsPayload.findIndex(
            (x) => x.fieldId === lastNameId && x.visible
          );

          if (lastNameId && lastNameIndex === -1) {
            columnSettingsPayload.splice(fullNameIndex + 1, 0, {
              fieldId: lastNameId,
              visible: true,
            });
          }
        }
        fieldIds = [
          ...columnSettingsPayload.reduce((acc, { fieldId, visible }) => {
            if (visible) {
              acc.add(fieldId);
            }
            return acc;
          }, new Set()),
        ];
      }
      if (selectedOption?.value === 'custom') {
        fieldIds = selectedFields.map((x) => x.value);
      }

      const payload = createPayload({
        key: summaryCacheKey,
        isContacts: objectInfo.isContacts,
        objectId: model.id,
        sendEmailNotification,
        data: {
          includeKizenId,
          summarizeRelationships,
          fields: fieldIds,
        },
      });
      await PerformActionService.exportRecords(payload);
      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'Export queued for {{formattedRecordCount}} {{entityName}} records',
          { entityName, formattedRecordCount }
        ),
      });
    } catch {
      showToast({
        variant: toastVariant.FAILURE,
        message:
          'The export was not successful. Please try again or contact Kizen support.',
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleArchive = async () => {
    try {
      setPerformingAction(true);
      const payload = createPayload({
        key: summaryCacheKey,
        isContacts: objectInfo.isContacts,
        objectId: model.id,
        sendEmailNotification,
      });
      await PerformActionService.archiveRecords(payload);
      const message = t(
        'Archive queued for {{formattedRecordCount}} {{entityName}} records',
        { entityName, formattedRecordCount }
      );
      showToast({
        variant: toastVariant.SUCCESS,
        message,
        delay: toastDelay.long,
      });
      onResetSelection();
    } catch {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The archive was not successful. Please try again or contact Kizen support.'
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleChangeFieldValue = async () => {
    try {
      setPerformingAction(true);
      const getValue = () => {
        switch (field.fieldType) {
          case FIELD_TYPES.Dropdown.type:
          case FIELD_TYPES.Radio.type:
          case FIELD_TYPES.Status.type:
            return isTimeZoneField(field) || isEmailStatusField(field)
              ? fieldValue.code
              : fieldValue.id || null;
          case FIELD_TYPES.Relationship.type:
          case FIELD_TYPES.Checkboxes.type:
          case FIELD_TYPES.Files.type:
            if (fieldValue) {
              if (Array.isArray(fieldValue) && fieldValue.length) {
                return fieldValue.map(({ id }) => id);
              }
              return fieldValue.id || null;
            }
            return null;
          case FIELD_TYPES.TeamSelector.type: {
            if (isContactTitlesField(field)) return getArrayOfIds(fieldValue);

            return fieldValue?.id || null;
          }
          case FIELD_TYPES.Decimal.type:
          case FIELD_TYPES.Choices.type:
          case FIELD_TYPES.Money.type:
            return String(fieldValue) || null;
          case FIELD_TYPES.Date.type:
          case FIELD_TYPES.DateTime.type:
            return getPayloadFieldValue(fieldValue, field) || null;
          case FIELD_TYPES.Text.type:
          case FIELD_TYPES.LongText.type:
            return fieldValue || '';
          case FIELD_TYPES.Integer.type:
            return typeof fieldValue === 'number' ? fieldValue : null;
          default: {
            return fieldValue || null;
          }
        }
      };

      const data = {
        field_id:
          field.isDefault && !isContactTitlesField(field)
            ? field.name
            : field.id,
        field_value: getValue(),
      };

      if (resolution) {
        data.field_resolution = resolution.value;
      }

      if (field.fieldType === FIELD_TYPES.DynamicTags.type) {
        delete data.field_value;
        data.tags_to_add = fieldValue ? fieldValue.map(({ id }) => id) : [];
        data.tags_to_remove = removeTags ? removeTags.map(({ id }) => id) : [];
      }

      const payload = createPayload({
        data,
        key: summaryCacheKey,
        isContacts: objectInfo.isContacts,
        objectId: model.id,
        sendEmailNotification,
      });
      await PerformActionService.changeFieldValue(payload);
      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          "'{{displayName}}' update queued for {{formattedRecordCount}} {{entityName}} records",
          { displayName: field.displayName, entityName, formattedRecordCount }
        ),
        delay: toastDelay.long,
      });
    } catch {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          "The '{{displayName}}' field was not successfully queued. Please try again or contact Kizen support.",
          {
            displayName: field.displayName,
          }
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleRunSmartConnectorAction = async () => {
    try {
      setPerformingAction(true);

      await SmartConnectorService.startSmartConnectorFlow(
        smartConnector.value,
        { entity_records_set_key: summaryCacheKey }
      );

      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'SmartConnector execution queued for {{formattedRecordCount}} {{entityName}} records',
          { entityName, formattedRecordCount }
        ),
        delay: toastDelay.long,
      });
    } catch (error) {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The SmartConnector execution was not successfully started. Please try again or contact Kizen support.'
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  const handleSubmit = () => {
    // block additional calls to getSummaryFn after commit
    confirmClickedRef.current = true;
    // Choose submit action based on selected action
    switch (selectedAction.value) {
      case MODIFY_AUTOMATION_IDENTIFIER:
        handleAutomationAction(secondaryAction);
        break;
      case START_AGENTIC_WORKFLOW_IDENTIFIER:
        handleAutomationAction(SECONDARY_ACTIONS.START);
        break;
      case CUSTOM_ACTION_IDENTIFIER:
        handleCustomAction();
        break;
      case SEND_SURVEY_IDENTIFIER:
        handleSendSurvey();
        break;
      case SEND_TEXT_IDENTIFIER:
        handleSendText();
        break;
      case SEND_EMAIL_IDENTIFIER:
        handleSendEmail();
        break;
      case CHANGE_TAGS_IDENTIFIER:
        handleChangeTags();
        break;
      case MANAGE_TEAM_IDENTIFIER:
        handleManageTeam();
        break;
      case MANAGE_SUBSCRIPTIONS:
        handleManageSubscriptions();
        break;
      case EXPORT_IDENTIFIER:
        handleExport();
        break;
      case ARCHIVE_IDENTIFIER:
        handleArchive();
        break;
      case CHANGE_FIELD_IDENTIFIER:
        handleChangeFieldValue();
        break;
      case SMART_CONNECTOR_IDENTIFIER:
        handleRunSmartConnectorAction();
        break;
      default: {
        console.debug('Action not recognized');
        break;
      }
    }
  };

  const handleDisableButton = useMemo(() => {
    if (selectedAction) {
      switch (selectedAction.value) {
        case SEND_SURVEY_IDENTIFIER:
          return !surveyValue;
        case MODIFY_AUTOMATION_IDENTIFIER:
          return !automation;
        case START_AGENTIC_WORKFLOW_IDENTIFIER:
          return !automation;
        case SEND_TEXT_IDENTIFIER:
          return !sendTextValue.content || !sendTextName;
        case SEND_EMAIL_IDENTIFIER:
          return !sendEmailStepIsValid(sendEmailStep);
        case CHANGE_TAGS_IDENTIFIER:
          return !(
            (tagsToAdd && tagsToAdd.length) ||
            (tagsToRemove && tagsToRemove.length)
          );
        case MANAGE_TEAM_IDENTIFIER:
          return teamMembers && !teamMembers.length;
        case MANAGE_SUBSCRIPTIONS:
          return !subscriptionItem;
        case EXPORT_IDENTIFIER: {
          if (selectedOption?.value === 'custom') {
            return !selectedFields.length;
          }
          return false;
        }
        case ARCHIVE_IDENTIFIER:
          return false;
        case CHANGE_FIELD_IDENTIFIER:
          if (field?.fieldType === FIELD_TYPES.DynamicTags.type) {
            return !(field && (fieldValue?.length || removeTags?.length));
          }

          if (field?.fieldType === FIELD_TYPES.Relationship.type) {
            if (!resolution) {
              return true;
            }

            if (resolution?.value === 'add_only') {
              return !fieldValue || fieldValue.length === 0;
            }

            return (
              field?.isRequired && (!fieldValue || fieldValue.length === 0)
            );
          }

          if (field?.isRequired && !fieldValue) {
            return !fieldValue;
          }

          if (
            [FIELD_TYPES.Files.type, FIELD_TYPES.Checkboxes.type].includes(
              field?.fieldType
            )
          ) {
            return !resolution;
          }
          if (
            field?.fieldType === FIELD_TYPES.TeamSelector.type &&
            !field?.isDefault
          ) {
            return false;
          }

          if (field?.fieldType === FIELD_TYPES.Email.type) {
            return isEmail.full(fieldValue);
          }

          return !field;
        case SMART_CONNECTOR_IDENTIFIER:
          return !smartConnector?.value;
        default: {
          return false;
        }
      }
    }
    return false;
  }, [
    automation,
    field,
    fieldValue,
    removeTags,
    resolution,
    selectedAction,
    sendEmailStep,
    sendTextValue.content,
    sendTextName,
    subscriptionItem,
    surveyValue,
    tagsToAdd,
    tagsToRemove,
    teamMembers,
    selectedOption?.value,
    selectedFields.length,
    smartConnector?.value,
  ]);

  const disableNextButton = isFetching || handleDisableButton;
  const disablePerformActionSubmit = !summaryCacheKey || performingAction;
  const isSelectionAllRecords = selection.checkedCount === allCount;
  const isLoadingSummary = isFetching || isPolling;

  return (
    <BasicModalWithConfirmation
      heading={`${title.affix} ${title.text}`}
      size="medium"
      onHide={onHide}
      dirty={dirty}
      hideFooter
      enforceFocus={false}
      {...props}
    >
      <StyledModalBody>
        {currentStep === 1 && (
          <GroupedActionButtons
            performActionOptions={performActionOptions}
            selectedOption={pendingAction}
            onSelect={setPendingAction}
            groupDefs={groupDefs}
          />
        )}
        {!(currentStep === 1) && (
          <PerformActionStepper currentStep={currentStep} steps={steps} />
        )}
        {currentStep === 2 && !disableConfigureActionStep && (
          <SelectedActionStep
            userAction={selectedAction}
            sendTextValue={sendTextValue.htmlContent}
            sendTextName={sendTextName}
            onSetSendTextValue={setSendTextValue}
            onSetSendTextName={setSendTextName}
            sendTextMergeFields={sendTextMergeFields}
            tagsToAdd={tagsToAdd}
            tagsToRemove={tagsToRemove}
            field={field}
            fieldValue={fieldValue}
            resolution={resolution}
            removeTags={removeTags}
            tagsField={tagsField}
            onAddTagsToAdd={setTagsToAdd}
            onAddTagsToRemove={setTagsToRemove}
            teamMembers={teamMembers}
            onAddTeamMembers={setTeamMembers}
            grantMyRecordsAccess={grantMyRecordsAccess}
            onGrantMyRecordsAccess={setGrantMyRecordsAccess}
            onSetSubscriptionItem={setSubscriptionItem}
            onSetSubscriptionStatus={setSubscriptionStatus}
            subscriptionItem={subscriptionItem}
            subscriptionStatus={status}
            setField={setField}
            setFieldValue={setFieldValue}
            setResolutionValue={setResolutionValue}
            setRemoveTags={setRemoveTags}
            sendEmailStep={sendEmailStep}
            surveyValue={surveyValue}
            setSurveyValue={setSurveyValue}
            automation={automation}
            setAutomation={setAutomation}
            model={model}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            includeKizenId={includeKizenId}
            setIncludeKizenId={setIncludeKizenId}
            summarizeRelationships={summarizeRelationships}
            setSummarizeRelationships={setSummarizeRelationships}
            secondaryAction={secondaryAction}
            setSecondaryAction={setSecondaryAction}
            resumePausedAutomations={resumePausedAutomations}
            setResumePausedAutomations={setResumePausedAutomations}
            smartConnector={smartConnector}
            setSmartConnector={setSmartConnector}
          />
        )}
        {currentStep === 3 && (
          <SummaryStep
            userAction={selectedAction}
            summary={summary?.stats && { ...summary.stats, text: title.text }}
            sendTextValue={sendTextValue.htmlContent}
            sendTextName={sendTextName}
            tagsToAdd={tagsToAdd}
            tagsToRemove={tagsToRemove}
            teamMembers={teamMembers}
            subscriptionItem={subscriptionItem}
            subscriptionStatus={status}
            fieldData={{ field, fieldValue, resolution, removeTags }}
            sendEmailStep={sendEmailStep}
            surveyValue={surveyValue}
            automation={automation}
            objectInfo={objectInfo}
            secondaryAction={secondaryAction}
            isLoadingSummary={isLoadingSummary}
            resumePausedAutomations={resumePausedAutomations}
            customAction={selectedAction?.customAction}
            sendEmailNotification={sendEmailNotification}
            setSendEmailNotification={setSendEmailNotification}
            smartConnector={smartConnector}
          />
        )}
      </StyledModalBody>
      {currentStep === 1 && (
        <StyledModalFooter showBorder={false}>
          <div className="button-box">
            <Button variant="text" color="blue" onClick={onHide}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => pendingAction && onSelect(pendingAction)}
              disabled={!pendingAction}
              data-qa-id="perform-action-next"
            >
              {t('Next')}
            </Button>
          </div>
        </StyledModalFooter>
      )}
      {!(currentStep === 1) && (
        <StyledModalFooter showBorder={false}>
          <div className="button-box">
            {currentStep === 2 && (
              <>
                <Button
                  variant="text"
                  color="blue"
                  onClick={goToSelectActionStep}
                >
                  {t('Back')}
                </Button>
                <Button onClick={goToSummaryStep} disabled={disableNextButton}>
                  {t('Next')}
                </Button>
              </>
            )}
            {currentStep === 3 && (
              <>
                <Button
                  variant="text"
                  color="blue"
                  onClick={() =>
                    disableConfigureActionStep
                      ? goToSelectActionStep()
                      : goToConfigureActionStep()
                  }
                >
                  {t('Back')}
                </Button>
                {!isLoadingSummary && !summaryCacheKey ? (
                  <Button onClick={onHide}>{t('Close')}</Button>
                ) : (
                  <Button
                    onClick={() => {
                      if (
                        selectedAction.value !== 'bulk_export_to_csv' &&
                        (isSelectionAllRecords ||
                          selection.checkedCount >
                            CONFIRM_BULK_ACTION_THRESHOLD)
                      ) {
                        setShowConfirmModal(true);
                      } else {
                        handleSubmit();
                      }
                    }}
                    disabled={disablePerformActionSubmit || !allowedRecordCount}
                  >
                    {t('Confirm')}
                  </Button>
                )}
              </>
            )}
          </div>
        </StyledModalFooter>
      )}
      {showConfirmModal && (
        <ConfrimBulkActionModal
          isAllRecordsAction={allCount === allowedRecordCount}
          recordCount={allowedRecordCount}
          onHide={() => setShowConfirmModal(false)}
          onConfirm={() => {
            setShowConfirmModal(false);
            handleSubmit();
          }}
        />
      )}
    </BasicModalWithConfirmation>
  );
}
